import { DragEvent, MouseEvent, useCallback, useMemo, useRef } from "react";

import { Alert, message } from "antd";
import { useSelector } from "react-redux";
import {
    Connection,
    Edge,
    EdgeChange,
    MarkerType,
    Node,
    NodeChange,
    OnConnectStartParams,
    ReactFlow,
    useReactFlow,
} from "reactflow";

import { dispatchCaptureEvent } from "@regal-voice/ui";
import { ShortcutsPopover } from "Components/elements/ShortcutsPopover/ShortcutsPopover";
import {
    FLOW_NODE_WIDTH,
    FLOW_NODE_HEIGHT,
    STICKY_NODE_WIDTH,
    STICKY_NODE_HEIGHT,
} from "Components/shared/Flow/constants";
import { type CanvasCopyPasteConfig, CanvasType, useCanvasCopyPaste } from "Hooks/useCanvasCopyPaste";
import { useRefWithEffect } from "Hooks/useRefWithEffect";
import { getLogger } from "Services/LoggingService";
import {
    addEdge,
    addNode,
    clearEditedNode,
    connectNodes,
    copySelection,
    editNodeProperties,
    editStickyNote,
    expandStickyNotes,
    removeElements,
    selectEdges,
    selectElements,
    selectNodes,
    selectSelection,
    selectTriggerNode,
} from "Services/state/journeys/JourneyStateSlice";
import { updateEdges, updateNodes } from "Services/state/journeys/Thunks";
import { useRVDispatch } from "Services/state/Storage";
import { NodeType, NodeTypeStrings } from "Types/Journey";
import { escapeRegExp } from "Utils/StringUtils/StringUtils";

import { recenterCanvas } from "../../../shared/FlowHelpers";
import { createEdge, createNode } from "../../../shared/helpers";
import { JourneyButtonEdge } from "../Edges/JourneyEdges";
import {
    ABTestNode,
    AgentSMSTaskNode,
    AssignTargetAgentNode,
    CallNode,
    CancelTaskNode,
    ConditionalNode,
    CustomTaskNode,
    DelayNode,
    ReminderNode,
    ScheduledCallbackNode,
    SmsNode,
    StickyNoteNode,
    TriggerNode,
    UpdateTaskNode,
    UpdateContactNode,
    WebhookTaskNode,
    // demo nodes
    EightByEightNode,
    RinglessVMNode,
    AgentlessCallNode,
    VoiceAINode,
    GenesysNode,
    TalkdeskNode,
    NiceNode,
    Five9Node,
    SMSAINode,
    CustomAITaskNode,
    SendEmailNode,
    EmailTaskNode,
    DecisionAINode,
} from "../Nodes/Nodes";
import { ButtonsPanel } from "../Stats/ButtonsPanel";
import { ExpandNotesButton } from "../Stats/ExpandNotesButton";
import { JourneyLiveStats } from "../Stats/JourneyLiveStats";

import "reactflow/dist/style.css";
// eslint-disable-next-line import/order
import styles from "./Flow.module.scss";

const logger = getLogger("Journey Builder");
const MAC_OS_IDENTIFIER = "Mac OS";
const PRO_OPTIONS = { hideAttribution: true };

const nodeTypes = {
    [NodeType.Trigger]: TriggerNode,
    [NodeType.Delay]: DelayNode,
    [NodeType.Call]: CallNode,
    [NodeType.SMS]: SmsNode,
    [NodeType.AgentSMSTask]: AgentSMSTaskNode,
    [NodeType.CustomTask]: CustomTaskNode,
    [NodeType.CancelTask]: CancelTaskNode,
    [NodeType.Conditional]: ConditionalNode,
    [NodeType.ABTest]: ABTestNode,
    [NodeType.Webhook]: WebhookTaskNode,
    [NodeType.AssignTargetAgent]: AssignTargetAgentNode,
    [NodeType.UpdateTask]: UpdateTaskNode,
    [NodeType.UpdateContact]: UpdateContactNode,
    [NodeType.Reminder]: ReminderNode,
    [NodeType.ScheduledCallback]: ScheduledCallbackNode,
    [NodeType.DEMO_EightByEight]: EightByEightNode,
    [NodeType.DEMO_RinglessVM]: RinglessVMNode,
    [NodeType.DEMO_AgentlessCall]: AgentlessCallNode,
    [NodeType.DEMO_VoiceAI]: VoiceAINode,
    [NodeType.DEMO_Genesys]: GenesysNode,
    [NodeType.DEMO_Talkdesk]: TalkdeskNode,
    [NodeType.DEMO_NICE]: NiceNode,
    [NodeType.DEMO_Five9]: Five9Node,
    [NodeType.DEMO_SMSAI]: SMSAINode,
    [NodeType.DEMO_CustomTaskAI]: CustomAITaskNode,
    [NodeType.DecisionAI]: DecisionAINode,
    [NodeType.DEMO_SendEmail]: SendEmailNode,
    [NodeType.DEMO_EmailTask]: EmailTaskNode,
    [NodeType.StickyNote]: StickyNoteNode,
};

const edgeTypes = {
    buttonedge: JourneyButtonEdge,
};

const defaultEdgeOptions = {
    markerEnd: { type: MarkerType.ArrowClosed },
};

export default function Flow({
    expand,
    isViewOnly = false,
    onViewExecutions,
}: {
    expand: () => void;
    isViewOnly?: boolean;
    onViewExecutions?: () => void;
}): JSX.Element {
    const flowRef = useRef<HTMLDivElement>(null);
    const { project } = useReactFlow();
    const dispatch = useRVDispatch();
    const nodes = useSelector(selectNodes);
    const edges = useSelector(selectEdges);
    const selection = useSelector(selectSelection);
    const triggerNode = useSelector(selectTriggerNode) as Node;
    const isMacOs = navigator.userAgent.indexOf(MAC_OS_IDENTIFIER) >= 0;

    // Use refs for frequently changing state to avoid recreating callbacks
    const nodesRef = useRefWithEffect(nodes);
    const edgesRef = useRefWithEffect(edges);
    const selectionRef = useRefWithEffect(selection);

    const copyPasteConfig = useMemo<CanvasCopyPasteConfig>(
        () => ({
            canvasType: CanvasType.JOURNEY,
            canUseCopyPaste: true,
            getSelectedElements: () => {
                // Handle null selection
                if (!selectionRef.current) {
                    return { nodes: [], edges: [] };
                }
                // Separate nodes and edges from selection
                const selectedNodes = selectionRef.current.filter((el) => !("source" in el)) as Node[];
                const selectedEdges = selectionRef.current.filter((el) => "source" in el) as Edge[];

                // Also include all edges that connect the selected nodes, even if not explicitly selected
                // This ensures we capture all edges when using box selection or multi-select
                const selectedNodeIds = new Set(selectedNodes.map((n) => n.id));
                const allConnectedEdges = edgesRef.current.filter(
                    (edge) => selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target)
                );

                // Merge explicitly selected edges with connected edges (deduplicate by id)
                const edgeMap = new Map<string, Edge>();
                [...selectedEdges, ...allConnectedEdges].forEach((edge) => {
                    edgeMap.set(edge.id, edge);
                });
                const finalEdges = Array.from(edgeMap.values());

                return { nodes: selectedNodes, edges: finalEdges };
            },
            onPaste: ({ nodes: clipboardNodes, edges: clipboardEdges }) => {
                // Prevent pasting trigger nodes (only one trigger allowed per journey)
                const hasTriggerNode = clipboardNodes.some((node) => node.type === NodeType.Trigger);
                if (hasTriggerNode) {
                    message.warning("Trigger nodes cannot be pasted");
                    return;
                }

                // Paste by using the existing copySelection action which duplicates selected nodes
                // First, select the nodes from clipboard by matching their IDs in the current canvas
                const currentNodeIds = new Set(nodesRef.current.map((n) => n.id));
                const nodesToSelect = clipboardNodes.filter((n) => currentNodeIds.has(n.id));

                // Constants for positioning new nodes
                const PASTE_OFFSET_X = 30;
                const PASTE_OFFSET_Y = 80;

                if (nodesToSelect.length > 0) {
                    // Nodes are in current canvas, duplicate them using Redux action
                    // Need to select both nodes AND edges from clipboard for copySelection to work properly
                    const edgesToSelect =
                        clipboardEdges?.filter(
                            (edge) => currentNodeIds.has(edge.source) && currentNodeIds.has(edge.target)
                        ) || [];
                    const elementsToSelect = [...nodesToSelect, ...edgesToSelect];
                    dispatch(selectElements(elementsToSelect));
                    dispatch(copySelection());
                } else {
                    // Nodes are from a different canvas, need to create new nodes
                    const oldToNewIdMap = new Map<string, string>();
                    const newNodesToSelect: Node[] = [];
                    const newEdgesToSelect: Edge[] = [];

                    clipboardNodes.forEach((node) => {
                        const position = {
                            x: node.position.x + PASTE_OFFSET_X,
                            y: node.position.y + PASTE_OFFSET_Y,
                        };
                        const newNode = createNode({ type: node.type as NodeType, position });
                        // Copy over the data from the clipboard node
                        newNode.data = { ...node.data, friendlyId: undefined };
                        // Mark node as selected from the start for proper React Flow synchronization
                        newNode.selected = true;
                        oldToNewIdMap.set(node.id, newNode.id);
                        newNodesToSelect.push(newNode);
                        dispatch(addNode(newNode));
                    });

                    // Recreate all edges from selection that have valid source/target mappings
                    // This matches the behavior of the copySelection Redux action
                    if (clipboardEdges && clipboardEdges.length > 0) {
                        clipboardEdges.forEach((edge) => {
                            const newSource = oldToNewIdMap.get(edge.source);
                            const newTarget = oldToNewIdMap.get(edge.target);

                            // Only recreate edge if both endpoints have valid mappings
                            if (newSource && newTarget) {
                                // Update sourceHandle and targetHandle if they reference old node IDs
                                let sourceHandle = edge.sourceHandle;
                                let targetHandle = edge.targetHandle;

                                // Replace ALL occurrences of the old node ID with the new one in handles
                                // Use global regex to handle cases where node ID appears multiple times
                                if (edge.sourceHandle?.startsWith(edge.source)) {
                                    const escapedSource = escapeRegExp(edge.source);
                                    sourceHandle = edge.sourceHandle.replace(new RegExp(escapedSource, "g"), newSource);
                                }
                                if (edge.targetHandle?.startsWith(edge.target)) {
                                    const escapedTarget = escapeRegExp(edge.target);
                                    targetHandle = edge.targetHandle.replace(new RegExp(escapedTarget, "g"), newTarget);
                                }

                                const newEdge = {
                                    ...createEdge({
                                        source: newSource,
                                        sourceHandle,
                                        target: newTarget,
                                    }),
                                    targetHandle,
                                    // Mark edge as selected from the start for proper React Flow synchronization
                                    selected: true,
                                };
                                newEdgesToSelect.push(newEdge);
                                dispatch(addEdge(newEdge));
                            }
                        });
                    }

                    // Select the newly pasted nodes and edges (matching copySelection behavior)
                    // First, immediately select them in Redux with the objects we created
                    const elementsToSelect = [...newNodesToSelect, ...newEdgesToSelect];
                    if (elementsToSelect.length > 0) {
                        // Immediate selection with the created elements
                        dispatch(selectElements(elementsToSelect));
                    }
                }
            },
            filterNodeBeforeCopy: (node) => node.type !== NodeType.Trigger && node.type !== NodeType.StickyNote,
        }),
        [dispatch, edgesRef, nodesRef, selectionRef]
    );

    // Use the copy/paste hook for keyboard shortcuts
    useCanvasCopyPaste(copyPasteConfig);

    const onDrop = useCallback(
        (event: DragEvent) => {
            event.preventDefault();
            const nodeType = event.dataTransfer.getData("application/reactflow") as NodeType;
            if (!NodeTypeStrings.includes(nodeType)) {
                return;
            }
            const targetRect = (event.target as HTMLElement).getBoundingClientRect();
            const type = nodeType as NodeType;
            const offsetX = nodeType !== NodeType.StickyNote ? FLOW_NODE_WIDTH : STICKY_NODE_WIDTH;
            const offsetY = nodeType !== NodeType.StickyNote ? FLOW_NODE_HEIGHT : STICKY_NODE_HEIGHT;
            const position = project({
                x: event.clientX - targetRect.x - offsetX / 2,
                y: event.clientY - targetRect.y - offsetY / 2,
            });
            const node = createNode({ type, position });
            dispatch(addNode(node));
            if (nodeType === NodeType.StickyNote) {
                // enter edit mode for sticky note
                dispatch(editStickyNote(node.id));
            }
        },
        [dispatch, project]
    );

    const onDragEnter = useCallback((event: DragEvent) => {
        event.preventDefault();
    }, []);

    const onDragOver = useCallback((event: DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    }, []);

    const onConnect = useCallback(
        (edge: Edge | Connection) => {
            const { source, sourceHandle, target } = edge;
            if (!!source && !!target && source != target) {
                const edge = createEdge({ source, sourceHandle, target });
                try {
                    dispatch(addEdge(edge));
                } catch {
                    message.warning("To avoid an endless loop, a building block cannot target its ancestor");
                }
            } else {
                logger.error("invalid edge", edge);
            }
        },
        [dispatch]
    );

    const onInit = useCallback(
        (reactFlowInstance: any) => {
            if (triggerNode) {
                recenterCanvas(triggerNode.position, flowRef.current, reactFlowInstance);
            }
        },
        [triggerNode]
    );

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            dispatchCaptureEvent();
            dispatch(updateNodes(changes));
        },
        [dispatch]
    );

    const onEdgesChange = useCallback(
        (changes: EdgeChange[]) => {
            dispatch(updateEdges(changes));
        },
        [dispatch]
    );

    const onNodeClick = useCallback(
        (_: MouseEvent, element: Node) => {
            const elementIsNotStickyNote = element.type !== NodeType.StickyNote;

            if (elementIsNotStickyNote) {
                expand();
            }

            if (element.type !== "buttonedge") {
                dispatch(clearEditedNode()); // clear form item
                if (elementIsNotStickyNote) {
                    dispatch(editNodeProperties(element));
                    dispatch(expandStickyNotes(false));
                }
            }
        },
        [dispatch, expand]
    );

    const onEdgeClick = useCallback(() => {
        expand();
        dispatch(selectElements(null));
        dispatch(expandStickyNotes(false));
        dispatch(clearEditedNode()); // clear form item
    }, [dispatch, expand]);

    const onPaneClick = useCallback(() => {
        dispatchCaptureEvent();
        dispatch(selectElements(null));
        dispatch(expandStickyNotes(false));
    }, [dispatch]);

    const onNodesDelete = useCallback(
        (nodes: Node[]) => {
            dispatch(removeElements(nodes));
            // clear selection
            dispatch(selectElements(null));
        },
        [dispatch]
    );
    const onEdgesDelete = useCallback(
        (edges: Edge[]) => {
            dispatch(removeElements(edges));
            // clear selection
            dispatch(selectElements(null));
        },
        [dispatch]
    );

    const onConnectStart = useCallback(
        (_: React.MouseEvent | React.TouchEvent, { handleType }: OnConnectStartParams) => {
            dispatch(connectNodes(handleType));
        },
        [dispatch]
    );

    const onConnectEnd = useCallback(
        (_: any) => {
            dispatch(connectNodes(null));
        },
        [dispatch]
    );
    const onNodeDragStart = useCallback(
        (_event: React.MouseEvent, node: Node, _nodes: Node[]) => {
            dispatch(clearEditedNode()); // clear form item
            const elementIsNotStickyNote = node.type !== NodeType.StickyNote;
            if (elementIsNotStickyNote) {
                expand();
                dispatch(editNodeProperties(node));
                dispatch(expandStickyNotes(false));
            }
        },
        [dispatch, expand]
    );

    return (
        <div className={styles.flowContainer} data-testid="flow-container">
            {isViewOnly && (
                <Alert
                    message="Node-level journey stats are not displayed in View Only mode."
                    type="info"
                    showIcon
                    className={styles.viewOnlyAlert}
                />
            )}
            <ReactFlow
                ref={flowRef}
                onInit={onInit}
                nodes={nodes}
                edges={edges}
                elementsSelectable={true}
                zoomOnDoubleClick={false}
                zoomOnScroll={false}
                panOnScroll={true}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                className={styles.reactFlow}
                selectionKeyCode={isMacOs ? "Meta" : "Control"}
                multiSelectionKeyCode="Shift"
                defaultEdgeOptions={defaultEdgeOptions}
                onConnect={onConnect}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragEnter={onDragEnter}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onPaneClick={onPaneClick}
                onMove={dispatchCaptureEvent}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodesDelete={onNodesDelete}
                onEdgesDelete={onEdgesDelete}
                onConnectStart={onConnectStart}
                onConnectEnd={onConnectEnd}
                onNodeDragStart={onNodeDragStart}
                proOptions={PRO_OPTIONS}
            />
            <ButtonsPanel>
                <JourneyLiveStats onViewExecutions={onViewExecutions} />
                <ExpandNotesButton />
            </ButtonsPanel>
            <ShortcutsPopover
                content={
                    <div>
                        Hold <strong>Command (Mac) or CTRL (Windows)</strong> to highlight an area of nodes <br />
                        Hold <strong>Shift</strong> to multiselect <br />
                        Press <strong>Command+C / CTRL+C</strong> to copy selected nodes <br />
                        Press <strong>Command+V / CTRL+V</strong> to paste copied nodes
                    </div>
                }
            />
        </div>
    );
}
